import type { Category, Todo } from "../../domain/entities.ts";
import type { AppNotification, Invitation, Root } from "../../domain/app-types.ts";
import { addDays } from "../tasks/recurrence.ts";
import { taskIsArchived } from "../tasks/task-visibility.ts";
import { escapeHtml } from "../../ui/text.ts";

export interface DashboardInput {
  tasks: Todo[];
  myDay: Todo[];
  categories: Category[];
  roots: Root[];
  notifications: AppNotification[];
  invitations: Invitation[];
  today: string;
  now: number;
  online: boolean;
  canEdit(task: Todo): boolean;
  formatDate(value: string): string;
  icon(name: string): string;
  calendarHtml?: string;
}

export interface DashboardModel {
  myDay: Todo[];
  attention: Todo[];
  upcoming: Todo[];
  completed: Todo[];
  notifications: AppNotification[];
  invitations: Invitation[];
}

function attentionRank(task: Todo, today: string): number {
  if (task.dueDate && task.dueDate < today) return 0;
  if (task.dueDate === today) return 1;
  return 2;
}

export function dashboardModel(input: DashboardInput): DashboardModel {
  const usable = input.tasks.filter(task => !task.deleted && !taskIsArchived(task, input.categories));
  const open = usable.filter(task => !task.completed);
  const myDay = input.myDay.filter(task => open.some(item => item.id === task.id));
  const myDayIds = new Set(myDay.map(task => task.id));
  const attention = open.filter(task => !myDayIds.has(task.id) && (task.dueDate && task.dueDate <= input.today || task.priority === "urgent"))
    .sort((a, b) => attentionRank(a, input.today) - attentionRank(b, input.today) || (a.dueDate || "9999").localeCompare(b.dueDate || "9999") || a.title.localeCompare(b.title));
  const featured = new Set([...myDay, ...attention].map(task => task.id));
  const lastDay = addDays(input.today, 7);
  const upcoming = open.filter(task => {
    const date = task.startDate || task.dueDate || task.startAt?.slice(0, 10);
    return !featured.has(task.id) && !!date && date > input.today && date <= lastDay;
  }).sort((a, b) => (a.startDate || a.dueDate || a.startAt || "").localeCompare(b.startDate || b.dueDate || b.startAt || "") || a.title.localeCompare(b.title));
  const since = input.now - 7 * 86_400_000;
  const completed = usable.filter(task => task.completed && (task.completedAt || task.updatedAt) >= since)
    .sort((a, b) => (b.completedAt || b.updatedAt) - (a.completedAt || a.updatedAt));
  const notifications = input.notifications.filter(item => !item.read && ["assignment", "mention", "reply", "approval"].includes(item.kind) && (!item.snoozed_until || Date.parse(item.snoozed_until) <= input.now))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  return { myDay, attention, upcoming, completed, notifications, invitations: input.invitations };
}

function location(task: Todo, input: DashboardInput): string {
  const root = input.roots.find(item => item.id === task.rootId)?.name || "Space";
  const collection = input.categories.find(item => item.id === task.categoryId)?.name;
  return collection ? `${root} · ${collection}` : root;
}

function taskRow(task: Todo, input: DashboardInput, note = ""): string {
  const date = task.dueDate ? input.formatDate(task.dueDate) : task.startDate ? input.formatDate(task.startDate) : "";
  const detail = [note, date].filter(Boolean).join(" · ");
  const complete = input.canEdit(task) && !task.completed ? `<button class="dashboard-task__complete" type="button" data-dashboard-complete="${escapeHtml(task.id)}" aria-label="Complete ${escapeHtml(task.title)}" title="Complete task">${input.icon("check")}</button>` : "";
  return `<li class="dashboard-task">${complete}<button class="dashboard-task__open" type="button" data-dashboard-open="${escapeHtml(task.id)}"><span class="dashboard-task__title">${escapeHtml(task.title)}</span><span class="dashboard-task__meta">${escapeHtml(location(task, input))}${detail ? ` · ${escapeHtml(detail)}` : ""}</span></button></li>`;
}

function section(title: string, tasks: Todo[], input: DashboardInput, empty: string, action?: string): string {
  if (!tasks.length && title !== "My Day") return `<details class="dashboard-empty-section" aria-label="${title}"><summary>${title}</summary><p class="dashboard-empty">${empty}</p></details>`;
  const row = (task: Todo) => taskRow(task, input, task.dueDate && task.dueDate < input.today && !task.completed ? "Overdue" : task.priority === "urgent" && !task.completed ? "Urgent" : "");
  const rows = tasks.slice(0, 5).map(row).join("");
  const extra = tasks.length > 5 ? `<details class="dashboard-section__more"><summary>Show ${tasks.length - 5} more</summary><ul class="dashboard-task-list">${tasks.slice(5).map(row).join("")}</ul></details>` : "";
  return `<section class="dashboard-section" aria-label="${title}"><div class="dashboard-section__head"><h2>${title}</h2>${tasks.length ? `<span>${tasks.length}</span>` : ""}</div>${rows ? `<ul class="dashboard-task-list">${rows}</ul>` : `<p class="dashboard-empty">${empty}</p>`}${extra}${action || ""}</section>`;
}

export function dashboardView(input: DashboardInput): string {
  const model = dashboardModel(input);
  const myDay = section("My Day", model.myDay, input, "Nothing chosen for today. Pick a few tasks to focus on.", `<button class="dashboard-section__link" type="button" data-action="plan-my-day">Choose tasks</button>`);
  const attention = section("Needs attention", model.attention, input, "Nothing needs immediate attention.");
  const upcoming = section("Upcoming", model.upcoming, input, "No dated tasks in the next seven days.");
  const recent = section("Recently completed", model.completed, input, "Nothing completed in the last seven days.");
  const collaboration = model.notifications.length || model.invitations.length ? `<section class="dashboard-section" aria-label="Collaboration"><div class="dashboard-section__head"><h2>Collaboration</h2><span>${model.notifications.length + model.invitations.length}</span></div>${!input.online ? `<p class="dashboard-offline">Last known activity · reconnect to refresh</p>` : ""}<ul class="dashboard-activity">${model.notifications.slice(0, 3).map(item => `<li><button type="button" data-action="notifications">${input.icon("bell")}<span>${escapeHtml(item.message)}</span></button></li>`).join("")}${model.invitations.slice(0, 2).map(item => `<li><button type="button" data-action="invitations">${input.icon("mail")}<span>Invitation to ${escapeHtml(item.root_name)}</span></button></li>`).join("")}</ul></section>` : "";
  return `<div class="dashboard" data-dashboard><div class="dashboard__lead">${myDay}${attention}</div><div class="dashboard__follow">${input.calendarHtml || ""}${upcoming}${collaboration}${recent}</div></div>`;
}
